import {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import PageShell from '../components/PageShell';
import {getVideoToken} from '../api/ReservationApi';

/** LiveKit video room for a confirmed consultation (patient or assigned dentist). */
export default function ConsultationRoom() {
  const {reservationId} = useParams();
  const navigate = useNavigate();
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const roomRef = useRef(null);
  const [state, setState] = useState('connecting');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {token, livekit_url: url} = await getVideoToken(reservationId);
        const {Room, RoomEvent, Track} = await import('livekit-client');
        if (cancelled) return;
        const room = new Room({adaptiveStream: true, dynacast: true});
        roomRef.current = room;
        room.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === Track.Kind.Video || track.kind === Track.Kind.Audio) {
            const el = track.attach();
            el.setAttribute('data-remote', 'true');
            remoteRef.current.appendChild(el);
          }
        });
        room.on(RoomEvent.TrackUnsubscribed, (track) => track.detach().forEach((el) => el.remove()));
        room.on(RoomEvent.Disconnected, () => setState('ended'));
        await room.connect(url, token);
        await room.localParticipant.enableCameraAndMicrophone();
        const cam = room.localParticipant.getTrackPublication(Track.Source.Camera);
        if (cam && cam.track && localRef.current) cam.track.attach(localRef.current);
        setState('connected');
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Could not join the consultation');
          setState('error');
        }
      }
    })();
    return () => {
      cancelled = true;
      if (roomRef.current) roomRef.current.disconnect();
    };
  }, [reservationId]);

  const leave = () => {
    if (roomRef.current) roomRef.current.disconnect();
    navigate('/virtual-consultation/reservation-record');
  };

  return (
    <PageShell title="Consultation" showBackButton={true}>
      <div className="container py-3">
        <p role="status">
          {state === 'connecting' && 'Connecting to your consultation…'}
          {state === 'connected' && 'Connected'}
          {state === 'ended' && 'The consultation has ended.'}
        </p>
        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}
        <div className="row g-2">
          <div className="col-12 col-md-8" ref={remoteRef} role="region" aria-label="Dentist video" />
          <div className="col-12 col-md-4">
            <video
              ref={localRef}
              autoPlay
              muted
              playsInline
              className="w-100 rounded"
              aria-label="Your camera"
            />
          </div>
        </div>
        <button type="button" className="btn btn-outline-danger mt-3" onClick={leave}>
          Leave
        </button>
      </div>
    </PageShell>
  );
}
